/**
 * Review UI model for beta contracts (ADR-0102 addendum 2 / 3). Pure: no
 * React, no network. Comment bodies and values are contract content —
 * display only, never logged.
 */

import type { ReviewThread } from '../../api.js';
import type { SuggestionInfo } from '@hyranai/regta-document-ir';

/** URL params the beta editor reads to open a field ("Gå till fältet"). */
export const FIELD_PARAM = 'falt';
export const BILAGA_PARAM = 'bilaga';

/** Statuses where comments and field requests can be written (server: COMMENT_WRITE_STATUSES). */
export const COMMENTABLE_STATUSES: ReadonlySet<string> = new Set(['draft', 'pending', 'in_review']);

export const MAIN_DOCUMENT = 'main';
/** Document key of a thread / suggestion: `main` or the attachment id. */
export const documentKey = (attachmentId: string | null | undefined): string => attachmentId ?? MAIN_DOCUMENT;
export const attachmentIdOf = (key: string): string | null => (key === MAIN_DOCUMENT ? null : key);

/**
 * What anyone who can view a beta contract may do with its review items
 * (ADR-0102 addendum 4): comment and decide suggestions in draft, pending
 * and in_review; propose new suggestions only in in_review. Read-only once
 * issued.
 */
export function reviewActions(status: string): { suggest: boolean; decide: boolean; comment: boolean } {
  const open = COMMENTABLE_STATUSES.has(status);
  return { suggest: status === 'in_review', decide: open, comment: open };
}

/** Where "Gå till fältet" lands: the Avtal tab, or the bilaga's form under Bilagor. */
export function fieldHref(contractId: string, key: string, attachmentId: string | null): string {
  const q = new URLSearchParams();
  q.set('tab', attachmentId ? 'bilagor' : 'avtal');
  if (attachmentId) q.set(BILAGA_PARAM, attachmentId);
  q.set(FIELD_PARAM, key);
  return `/contracts/${contractId}/beta?${q.toString()}`;
}

export type ReviewView = 'uppgifter' | 'text';

export function reviewHref(contractId: string, view: ReviewView = 'uppgifter', attachmentId: string | null = null): string {
  const q = new URLSearchParams();
  q.set('vy', view);
  if (attachmentId) q.set('dokument', attachmentId);
  return `/contracts/${contractId}/beta/granska?${q.toString()}`;
}

/**
 * "Ändra i formuläret": `direct` in draft, `confirm` in pending / in_review
 * (the contract returns to draft first), `null` when values cannot change.
 */
export function formNavigationMode(status: string): 'direct' | 'confirm' | null {
  if (status === 'draft') return 'direct';
  if (status === 'pending' || status === 'in_review') return 'confirm';
  return null;
}

/** The confirm before a field navigation returns the contract to draft. */
export function returnToDraftConfirm(status: string, approved: boolean): string {
  const parts = ['Avtalet går tillbaka till utkast så att uppgiften kan ändras i formuläret.'];
  if (status === 'in_review') parts.push('Den pågående granskningen avbryts.');
  if (approved) parts.push('Godkännandet återställs och avtalet behöver granskas igen.');
  parts.push('Öppna kommentarer och förslag finns kvar.');
  return parts.join(' ');
}

/** A stored value as review text. Values are contract content: display only. */
export function formatReviewValue(value: unknown): string {
  if (value == null || value === '') return '(tomt)';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nej';
  if (Array.isArray(value)) return value.length === 0 ? '(tomt)' : value.map(formatReviewValue).join(', ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '(värde)';
    }
  }
  return String(value);
}

/** "Värdet ändrat sedan begäran: <old> → <new>", or null when unchanged / not a field request. */
export function valueChangedText(thread: Pick<ReviewThread, 'kind' | 'value_changed' | 'field_value_at_request' | 'current_value'>): string | null {
  if (thread.kind !== 'field_request' || !thread.value_changed) return null;
  return `Värdet ändrat sedan begäran: ${formatReviewValue(thread.field_value_at_request)} → ${formatReviewValue(thread.current_value)}`;
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

export const suggestionsLabel = (n: number): string => plural(n, 'ändringsförslag', 'ändringsförslag');
export const fieldRequestsLabel = (n: number): string => plural(n, 'begäran om ändrad uppgift', 'begäranden om ändrade uppgifter');
export const commentsLabel = (n: number): string => plural(n, 'kommentar', 'kommentarer');

export type OpenReviewItems = {
  openSuggestions: number;
  openFieldRequests: number;
  byDocument: Array<{ attachmentId: string | null; count: number }>;
};

/** The counts of a signing refusal `open_review_items` (412), or null for any other error. */
export function openReviewItemsOf(e: unknown): OpenReviewItems | null {
  const err = e as { message?: unknown; data?: Record<string, unknown> } | null | undefined;
  const data = err?.data;
  if (err?.message !== 'open_review_items' && data?.['reason'] !== 'open_review_items') return null;
  const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const docs = Array.isArray(data?.['suggestions_by_document']) ? (data['suggestions_by_document'] as unknown[]) : [];
  return {
    openSuggestions: num(data?.['open_suggestions']),
    openFieldRequests: num(data?.['open_field_requests']),
    byDocument: docs.flatMap((d) => {
      if (!d || typeof d !== 'object') return [];
      const r = d as Record<string, unknown>;
      return [{ attachmentId: typeof r['attachment_id'] === 'string' ? r['attachment_id'] : null, count: num(r['count']) }];
    }),
  };
}

/** Why signing is blocked, or null when nothing is open. */
export function signingGateText(items: Pick<OpenReviewItems, 'openSuggestions' | 'openFieldRequests'>): string | null {
  const parts: string[] = [];
  if (items.openSuggestions > 0) parts.push(`${suggestionsLabel(items.openSuggestions)} väntar på beslut`);
  if (items.openFieldRequests > 0) parts.push(`${fieldRequestsLabel(items.openFieldRequests)} är inte stängd${items.openFieldRequests === 1 ? '' : 'a'}`);
  if (parts.length === 0) return null;
  return `Avtalet kan inte skickas för signering: ${parts.join(' och ')}. Godta eller avvisa förslagen och stäng begärandena på granskningssidan.`;
}

/** The block instance of a node address (`rent#hyra/2` → `rent`), or null for `@seq` output. */
export function instanceOfAddress(address: string | null | undefined): string | null {
  if (!address || address.startsWith('@')) return null;
  const head = address.split(/[#/]/, 1)[0] ?? '';
  if (!head) return null;
  try {
    return decodeURIComponent(head);
  } catch {
    return null;
  }
}

/** Where a margin marker sits in the preview. */
export type MarkerTarget =
  | { kind: 'field'; key: string }
  | { kind: 'instance'; instanceId: string }
  | { kind: 'document' };

export type PreviewMarker = {
  id: string;
  documentKey: string;
  target: MarkerTarget;
  comments: number;
  suggestions: number;
  fieldRequests: number;
};

const targetId = (t: MarkerTarget): string =>
  t.kind === 'field' ? `field:${t.key}` : t.kind === 'instance' ? `instance:${t.instanceId}` : 'document';

/**
 * Margin markers for open threads and open suggestions, merged per
 * document and target (a field, a block instance, or the document).
 * Action-anchored comments sit on their suggestion's node when known.
 */
export function previewMarkers(
  threads: ReadonlyArray<Pick<ReviewThread, 'attachment_id' | 'anchor_kind' | 'anchor' | 'kind' | 'resolved'>>,
  suggestionsByDocument: ReadonlyMap<string, ReadonlyArray<Pick<SuggestionInfo, 'seq' | 'address' | 'open'>>>,
  changeAddresses?: ReadonlyMap<string, ReadonlyMap<number, string>>,
): PreviewMarker[] {
  const out = new Map<string, PreviewMarker>();
  const bump = (doc: string, target: MarkerTarget, field: 'comments' | 'suggestions' | 'fieldRequests') => {
    const id = `${doc}|${targetId(target)}`;
    const m = out.get(id) ?? { id, documentKey: doc, target, comments: 0, suggestions: 0, fieldRequests: 0 };
    m[field] += 1;
    out.set(id, m);
  };
  const nodeTarget = (address: string | null | undefined): MarkerTarget => {
    const instanceId = instanceOfAddress(address);
    return instanceId ? { kind: 'instance', instanceId } : { kind: 'document' };
  };
  for (const [doc, list] of suggestionsByDocument) {
    for (const s of list) if (s.open) bump(doc, nodeTarget(s.address), 'suggestions');
  }
  for (const t of threads) {
    if (t.resolved) continue;
    const doc = documentKey(t.attachment_id);
    if (t.anchor_kind === 'field' && t.anchor) {
      bump(doc, { kind: 'field', key: t.anchor }, t.kind === 'field_request' ? 'fieldRequests' : 'comments');
    } else if (t.anchor_kind === 'node') {
      bump(doc, nodeTarget(t.anchor), 'comments');
    } else if (t.anchor_kind === 'action') {
      const seq = Number(t.anchor);
      const s = suggestionsByDocument.get(doc)?.find((x) => x.seq === seq);
      bump(doc, nodeTarget(s?.address ?? changeAddresses?.get(doc)?.get(seq)), 'comments');
    } else {
      bump(doc, { kind: 'document' }, 'comments');
    }
  }
  return [...out.values()];
}

/** The marker's tooltip. */
export function markerTitle(m: Pick<PreviewMarker, 'comments' | 'suggestions' | 'fieldRequests'>): string {
  const parts: string[] = [];
  if (m.suggestions > 0) parts.push(suggestionsLabel(m.suggestions));
  if (m.fieldRequests > 0) parts.push(fieldRequestsLabel(m.fieldRequests));
  if (m.comments > 0) parts.push(commentsLabel(m.comments));
  return parts.join(', ');
}

/** Open threads per document in the given document order (others last). */
export function groupThreadsByDocument<T extends Pick<ReviewThread, 'attachment_id'>>(
  threads: readonly T[],
  order: readonly string[],
): Array<{ documentKey: string; threads: T[] }> {
  const groups = new Map<string, T[]>();
  for (const key of order) groups.set(key, []);
  for (const t of threads) {
    const key = documentKey(t.attachment_id);
    groups.set(key, [...(groups.get(key) ?? []), t]);
  }
  return [...groups.entries()].filter(([, list]) => list.length > 0).map(([key, list]) => ({ documentKey: key, threads: list }));
}

/** Open suggestions in group order: one row per `groupId`, first-seen order. */
export function suggestionGroups<S extends Pick<SuggestionInfo, 'groupId' | 'open'>>(list: readonly S[]): Array<{ groupId: string; items: S[] }> {
  const groups = new Map<string, S[]>();
  for (const s of list) groups.set(s.groupId, [...(groups.get(s.groupId) ?? []), s]);
  return [...groups.entries()].map(([groupId, items]) => ({ groupId, items }));
}

/**
 * One change in a document's change lists: an adaptation group (every seq
 * of the group) or a suggestion. `anchorSeq` is where a new comment on the
 * change anchors.
 */
export type ChangeRef = { key: string; seqs: readonly number[]; address: string | null; anchorSeq: number };

export type ThreadAssignment<T> = {
  /** Threads per change key: action threads on any of its seqs, node threads on its address. */
  byChange: Map<string, T[]>;
  /** Node threads on a node without a listed change. */
  nodes: T[];
  /** Action threads whose seq is in no listed change (gone from the log, or reverted). */
  actions: T[];
  document: T[];
};

type AnchoredThread = Pick<ReviewThread, 'anchor_kind' | 'anchor'>;

/** The first listed change containing `seq`. */
export function changeForSeq<C extends Pick<ChangeRef, 'seqs'>>(changes: readonly C[], seq: number): C | null {
  return changes.find((c) => c.seqs.includes(seq)) ?? null;
}

/** The first listed change on the node at `address`. */
export function changeForAddress<C extends Pick<ChangeRef, 'address'>>(changes: readonly C[], address: string): C | null {
  return changes.find((c) => c.address != null && c.address === address) ?? null;
}

/**
 * Places each thread with its change (first listed change wins, so list
 * open suggestions before adaptation groups before decided suggestions).
 * Field threads are not part of the text and are dropped.
 */
export function assignThreads<T extends AnchoredThread>(threads: readonly T[], changes: readonly ChangeRef[]): ThreadAssignment<T> {
  const out: ThreadAssignment<T> = { byChange: new Map(), nodes: [], actions: [], document: [] };
  const put = (key: string, t: T) => out.byChange.set(key, [...(out.byChange.get(key) ?? []), t]);
  for (const t of threads) {
    if (t.anchor_kind === 'action') {
      const seq = Number(t.anchor);
      const c = Number.isInteger(seq) ? changeForSeq(changes, seq) : null;
      if (c) put(c.key, t);
      else out.actions.push(t);
    } else if (t.anchor_kind === 'node') {
      const c = t.anchor ? changeForAddress(changes, t.anchor) : null;
      if (c) put(c.key, t);
      else out.nodes.push(t);
    } else if (t.anchor_kind === 'document') {
      out.document.push(t);
    }
  }
  return out;
}

export type ChangeCommentCount = { total: number; open: number };

/** Comment counts per seq of every change that has threads (for the document's badges). */
export function commentCountsBySeq(
  changes: readonly ChangeRef[],
  byChange: ReadonlyMap<string, ReadonlyArray<Pick<ReviewThread, 'resolved'>>>,
): Map<number, ChangeCommentCount> {
  const out = new Map<number, ChangeCommentCount>();
  for (const c of changes) {
    const list = byChange.get(c.key);
    if (!list?.length) continue;
    const count = { total: list.length, open: list.filter((t) => !t.resolved).length };
    for (const seq of c.seqs) if (!out.has(seq)) out.set(seq, count);
  }
  return out;
}
